'use client';

import * as React from 'react';
import type { PostCardFields } from 'app/blog/[domain]/(standalone)/_server/types';
import { FormattedDate } from 'components/formatted-date';
import { IconArrowRight } from 'icon/Arrows/arrow-right';
import { useAtomValue } from 'jotai';
import { useTranslations } from 'next-intl';
import { PostAvatar } from '../_components/post-avatar';
import { defaultAuthorAtom } from '../../_components/design-state';
import { PostCard } from './post-card';
import { PostLink } from './post-link';

export interface FeaturedPostProps {
  post: PostCardFields;
}

export function FeaturedPost({ post }: FeaturedPostProps): JSX.Element {
  const finalPost = {
    ...post,
  };
  return (
    <section
      id="featuredPost"
      style={{ display: 'var(--featured-card-display, block)' }}
    >
      <DesktopFeaturedPost post={finalPost} className="featured-post-desktop" />
      <PostCard
        post={finalPost}
        className="featured-post-mobile"
        aria-label="Featured post"
      />
    </section>
  );
}

type DesktopFeaturedPostProps = {
  post: PostCardFields;
  className?: string;
};

function DesktopFeaturedPost({
  post,
  className,
}: DesktopFeaturedPostProps): JSX.Element {
  const t = useTranslations('Blog.FeaturedPost');
  const hasTag = post.tags.length > 0;
  const author = useAtomValue(defaultAuthorAtom);
  return (
    <PostLink
      href={`/post/${post.slug}_${post.id}`}
      className={['featured-post-desktop', className].filter(Boolean).join(' ')}
      aria-label={t('AriaLabel')}
    >
      <div
        className="featured-post-image"
        style={{
          backgroundImage: `url(${post.coverImage})`,
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          backgroundPosition: '50% 50%',
        }}
      />
      <div className="featured-post-overlay">
        <div className="featured-post-heading">
          <h2 className="featured-post-title">{post.title}</h2>
          <IconArrowRight className="featured-post-arrow" />
        </div>
        <p className="featured-post-excerpt">{post.excerpt}</p>
        <div className="featured-post-meta">
          <div className="featured-post-byline">
            <div>
              <p className="featured-post-label">{t('WrittenBy')}</p>
              <div className="featured-post-author-profile">
                <PostAvatar name={author.name} avatar={author.avatar} />
                <div>
                  <p className="featured-post-author-name">{author.name}</p>
                  <p
                    className="featured-post-author-title"
                    title={author.title || ''}
                  >
                    {author.title}
                  </p>
                </div>
              </div>
            </div>
            <div>
              <p className="featured-post-label">{t('PublishedOn')}</p>
              <p className="featured-post-published-date">
                <FormattedDate date={post.publishedAt} />
              </p>
            </div>
          </div>
          <div>
            {hasTag && (
              <div>
                <p className="featured-post-label">{t('FiledUnder')}</p>
                <div className="featured-post-tag-list">
                  {post.tags.slice(0, 6).map((tag) => (
                    <p className="featured-post-tag" key={tag}>
                      {tag}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </PostLink>
  );
}
